import React, { useState, useEffect, useCallback } from 'react';
import API from '../services/api';

const SERVICE_TYPES = ['INDIVIDUAL', 'COUPLES', 'GROUP', 'FAMILY'];
const SERVICE_LABELS = {
  INDIVIDUAL: 'Individual', COUPLES: 'Couples', GROUP: 'Group', FAMILY: 'Family',
};

const S = {
  page: {
    margin: '2rem auto', maxWidth: 1280, padding: '0 1.5rem',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    color: '#1c1f24',
  },
  h1: { fontSize: '1.6rem', fontWeight: 600, margin: '0 0 0.25rem', letterSpacing: '-0.02em' },
  subtle: { color: '#6b7280', fontSize: '0.9rem' },
  card: {
    background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10,
    padding: '1.25rem 1.5rem', marginBottom: '1.25rem',
    boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
  },
  cardTitle: {
    fontSize: '0.85rem', fontWeight: 600, textTransform: 'uppercase',
    letterSpacing: '0.08em', color: '#475569', margin: '0 0 0.9rem',
  },
  field: { display: 'flex', flexDirection: 'column', gap: '0.25rem' },
  label: { fontSize: '0.78rem', fontWeight: 500, color: '#475569' },
  input: {
    padding: '0.5rem 0.65rem', border: '1px solid #d1d5db', borderRadius: 6,
    fontSize: '0.9rem', background: '#fff', fontFamily: 'inherit',
  },
  grid3: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem' },
  grid4: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.75rem' },
  filterRow: {
    display: 'grid', gridTemplateColumns: '1fr 1fr 1.5fr 1fr auto', gap: '0.75rem',
    alignItems: 'end',
  },
  btn: {
    padding: '0.5rem 0.95rem', border: '1px solid #d1d5db', borderRadius: 6,
    background: '#fff', color: '#1c1f24', fontSize: '0.875rem', fontWeight: 500,
    cursor: 'pointer', fontFamily: 'inherit',
  },
  btnPrimary: { background: '#1c1f24', color: '#fff', borderColor: '#1c1f24' },
  btnGhost: {
    background: 'transparent', border: '1px solid transparent', color: '#475569',
    fontSize: '0.82rem',
  },
  btnDisabled: { opacity: 0.45, cursor: 'not-allowed' },
  table: { width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: '0.88rem' },
  th: {
    textAlign: 'left', padding: '0.55rem 0.75rem', fontWeight: 600, color: '#475569',
    borderBottom: '2px solid #e5e7eb', fontSize: '0.78rem', textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  td: { padding: '0.55rem 0.75rem', borderBottom: '1px solid #f1f5f9' },
  tdNum: { padding: '0.55rem 0.75rem', borderBottom: '1px solid #f1f5f9', textAlign: 'right' },
  badge: {
    display: 'inline-block', padding: '0.1rem 0.5rem', borderRadius: 999,
    fontSize: '0.72rem', fontWeight: 600, background: '#fef3c7', color: '#92400e',
  },
  blockerBox: {
    background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b',
    borderRadius: 8, padding: '0.75rem 1rem', marginBottom: '1rem', fontSize: '0.88rem',
  },
  warnBox: {
    background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e',
    borderRadius: 8, padding: '0.75rem 1rem', marginBottom: '1rem', fontSize: '0.88rem',
  },
  totalsRow: { fontWeight: 600, background: '#fafafa' },
};

const fmtMoney = (n) =>
  (Number(n) || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

function AdminQuickBooksExportPage() {
  /* ---------------- settings ---------------- */
  const [therapists, setTherapists] = useState([]);
  const [arAccount, setArAccount] = useState('');
  const [incomeAccount, setIncomeAccount] = useState('');
  const [depositAccount, setDepositAccount] = useState('');
  const [itemMap, setItemMap] = useState({});
  const [classMap, setClassMap] = useState({});
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);

  /* ---------------- filters / preview ---------------- */
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [therapistId, setTherapistId] = useState('');
  const [paymentType, setPaymentType] = useState('');
  const [includeExported, setIncludeExported] = useState(false);
  const [preview, setPreview] = useState(null); // { sessions, totals, blockers, warnings }
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await API.get('/admin/qb-export/settings');
        const s = res.data.settings;
        setTherapists(res.data.therapists);
        setArAccount(s.ar_account || '');
        setIncomeAccount(s.income_account || '');
        setDepositAccount(s.deposit_account || '');
        setItemMap(s.item_map || {});
        setClassMap(s.class_map || {});
        // Open the panel until it's filled in, then stay out of the way.
        const incomplete =
          !s.income_account ||
          SERVICE_TYPES.some((t) => !(s.item_map || {})[t]) ||
          res.data.therapists.some((t) => !(s.class_map || {})[String(t.id)]);
        setSettingsOpen(incomplete);
      } catch (e) {
        console.error(e);
        alert('Error loading QuickBooks export settings.');
      }
    })();
  }, []);

  async function saveSettings() {
    setSavingSettings(true);
    try {
      await API.put('/admin/qb-export/settings', {
        arAccount, incomeAccount, depositAccount, itemMap, classMap,
      });
      if (preview) await loadPreview();
      alert('Settings saved.');
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.message || 'Error saving settings.');
    } finally {
      setSavingSettings(false);
    }
  }

  const loadPreview = useCallback(async () => {
    if (!startDate || !endDate) {
      alert('Pick a start and end date first.');
      return;
    }
    setBusy(true);
    try {
      const res = await API.get('/admin/qb-export/preview', {
        params: {
          start: startDate, end: endDate,
          therapistId: therapistId || undefined,
          paymentType: paymentType || undefined,
          includeExported: includeExported ? 'true' : 'false',
        },
      });
      setPreview(res.data);
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.message || 'Error loading preview.');
    } finally {
      setBusy(false);
    }
  }, [startDate, endDate, therapistId, paymentType, includeExported]);

  async function downloadIif() {
    setBusy(true);
    try {
      const res = await API.post(
        '/admin/qb-export/generate',
        {
          start: startDate, end: endDate,
          therapistId: therapistId || undefined,
          paymentType: paymentType || undefined,
          includeExported,
        },
        { responseType: 'blob' }
      );
      const blob = new Blob([res.data], { type: 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `qb-export-${startDate}_to_${endDate}.iif`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      await loadPreview(); // just-exported rows drop out (or gain their badge)
    } catch (e) {
      console.error(e);
      let msg = 'Error generating the export file. Reload the preview and check for blockers.';
      if (e?.response?.data instanceof Blob) {
        try {
          const json = JSON.parse(await e.response.data.text());
          if (json?.message) msg = json.message;
        } catch (_) { /* keep generic message */ }
      }
      alert(msg);
    } finally {
      setBusy(false);
    }
  }

  const blockers = preview?.blockers || [];
  const warnings = preview?.warnings || [];
  const sessions = preview?.sessions || [];
  const canDownload = sessions.length > 0 && blockers.length === 0 && !busy;

  // Render item-map inputs for the known types plus anything already saved.
  const itemKeys = Array.from(new Set([...SERVICE_TYPES, ...Object.keys(itemMap)]));

  return (
    <div style={S.page}>
      <h1 style={S.h1}>QuickBooks Export</h1>
      <p style={S.subtle}>
        Download PAID sessions as a QuickBooks Desktop IIF file (invoices + payments).
        Import in QuickBooks via File &gt; Utilities &gt; Import &gt; IIF Files.
        Names below must exactly match your QuickBooks lists — QuickBooks silently
        creates new entries for anything it doesn't recognize.
      </p>

      {/* ---------------- settings ---------------- */}
      <div style={S.card}>
        <h2 style={S.cardTitle}>
          QuickBooks names{' '}
          <button style={{ ...S.btn, ...S.btnGhost, padding: '0.1rem 0.5rem' }}
            onClick={() => setSettingsOpen((o) => !o)}>
            {settingsOpen ? 'Hide' : 'Edit'}
          </button>
        </h2>
        {settingsOpen && (
          <>
            <div style={{ ...S.grid3, marginBottom: '1rem' }}>
              <div style={S.field}>
                <label style={S.label}>Accounts Receivable account</label>
                <input style={S.input} value={arAccount}
                  onChange={(e) => setArAccount(e.target.value)} />
              </div>
              <div style={S.field}>
                <label style={S.label}>Income account</label>
                <input style={S.input} value={incomeAccount}
                  placeholder="e.g. Counseling Income"
                  onChange={(e) => setIncomeAccount(e.target.value)} />
              </div>
              <div style={S.field}>
                <label style={S.label}>Deposit-to account</label>
                <input style={S.input} value={depositAccount}
                  onChange={(e) => setDepositAccount(e.target.value)} />
              </div>
            </div>

            <div style={{ ...S.grid4, marginBottom: '1rem' }}>
              {itemKeys.map((t) => (
                <div style={S.field} key={t}>
                  <label style={S.label}>Item for {SERVICE_LABELS[t] || t}</label>
                  <input style={S.input} value={itemMap[t] || ''}
                    placeholder="QuickBooks item name"
                    onChange={(e) => setItemMap((m) => ({ ...m, [t]: e.target.value }))} />
                </div>
              ))}
            </div>

            <div style={{ ...S.grid4, marginBottom: '1rem' }}>
              {therapists.map((t) => (
                <div style={S.field} key={t.id}>
                  <label style={S.label}>Class for {t.first_name} {t.last_name}</label>
                  <input style={S.input} value={classMap[String(t.id)] || ''}
                    placeholder="e.g. EJR"
                    onChange={(e) =>
                      setClassMap((m) => ({ ...m, [String(t.id)]: e.target.value }))} />
                </div>
              ))}
            </div>

            <button
              style={{ ...S.btn, ...S.btnPrimary, ...(savingSettings ? S.btnDisabled : {}) }}
              disabled={savingSettings} onClick={saveSettings}>
              {savingSettings ? 'Saving…' : 'Save settings'}
            </button>
          </>
        )}
      </div>

      {/* ---------------- filters ---------------- */}
      <div style={S.card}>
        <h2 style={S.cardTitle}>Filters</h2>
        <div style={S.filterRow}>
          <div style={S.field}>
            <label style={S.label}>Start date</label>
            <input type="date" style={S.input} value={startDate}
              onChange={(e) => { setStartDate(e.target.value); setPreview(null); }} />
          </div>
          <div style={S.field}>
            <label style={S.label}>End date</label>
            <input type="date" style={S.input} value={endDate}
              onChange={(e) => { setEndDate(e.target.value); setPreview(null); }} />
          </div>
          <div style={S.field}>
            <label style={S.label}>Therapist</label>
            <select style={S.input} value={therapistId}
              onChange={(e) => { setTherapistId(e.target.value); setPreview(null); }}>
              <option value="">All therapists</option>
              {therapists.map((t) => (
                <option key={t.id} value={t.id}>{t.first_name} {t.last_name}</option>
              ))}
            </select>
          </div>
          <div style={S.field}>
            <label style={S.label}>Payment type</label>
            <select style={S.input} value={paymentType}
              onChange={(e) => { setPaymentType(e.target.value); setPreview(null); }}>
              <option value="">All</option>
              <option value="CASH">Cash</option>
              <option value="CHECK">Check</option>
              <option value="CREDIT_CARD">Credit Card</option>
            </select>
          </div>
          <button style={{ ...S.btn, ...(busy ? S.btnDisabled : {}) }}
            disabled={busy} onClick={loadPreview}>
            {busy ? 'Loading…' : 'Preview'}
          </button>
        </div>
        <label style={{ ...S.subtle, display: 'inline-flex', gap: '0.4rem', marginTop: '0.75rem' }}>
          <input type="checkbox" checked={includeExported}
            onChange={(e) => { setIncludeExported(e.target.checked); setPreview(null); }} />
          Include sessions already exported to QuickBooks (for redos — will create
          duplicates in QuickBooks if the originals are still there)
        </label>
      </div>

      {/* ---------------- preview ---------------- */}
      {preview && (
        <div style={S.card}>
          <h2 style={S.cardTitle}>Preview</h2>

          {blockers.length > 0 && (
            <div style={S.blockerBox}>
              <strong>Export blocked:</strong>
              <ul style={{ margin: '0.4rem 0 0 1.2rem' }}>
                {blockers.map((b) => <li key={b.kind + b.key}>{b.message}</li>)}
              </ul>
            </div>
          )}
          {warnings.length > 0 && (
            <div style={S.warnBox}>
              <ul style={{ margin: '0 0 0 1.2rem' }}>
                {warnings.map((w) => <li key={w.kind + w.key}>{w.message}</li>)}
              </ul>
            </div>
          )}

          {sessions.length === 0 ? (
            <p style={S.subtle}>No sessions match these filters.</p>
          ) : (
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Date</th>
                  <th style={S.th}>Client</th>
                  <th style={S.th}>Therapist</th>
                  <th style={S.th}>Service</th>
                  <th style={{ ...S.th, textAlign: 'right' }}>Billed</th>
                  <th style={{ ...S.th, textAlign: 'right' }}>Collected</th>
                  <th style={S.th}>Payment</th>
                  <th style={S.th}></th>
                </tr>
              </thead>
              <tbody>
                {sessions.map((s) => (
                  <tr key={s.id}>
                    <td style={S.td}>{s.session_date}</td>
                    <td style={S.td}>{s.client_name}</td>
                    <td style={S.td}>{s.therapist_name}</td>
                    <td style={S.td}>{SERVICE_LABELS[s.service_type] || s.service_type}</td>
                    <td style={S.tdNum}>{fmtMoney(s.billed_amount)}</td>
                    <td style={S.tdNum}>{fmtMoney(s.collected_amount)}</td>
                    <td style={S.td}>{s.payment_type || '—'}</td>
                    <td style={S.td}>
                      {s.qb_exported_at && <span style={S.badge}>exported</span>}
                    </td>
                  </tr>
                ))}
                <tr style={S.totalsRow}>
                  <td style={S.td} colSpan={4}>{preview.totals.count} sessions</td>
                  <td style={S.tdNum}>{fmtMoney(preview.totals.billed)}</td>
                  <td style={S.tdNum}>{fmtMoney(preview.totals.collected)}</td>
                  <td style={S.td} colSpan={2}></td>
                </tr>
              </tbody>
            </table>
          )}

          <div style={{ marginTop: '1rem' }}>
            <button
              style={{ ...S.btn, ...S.btnPrimary, ...(canDownload ? {} : S.btnDisabled) }}
              disabled={!canDownload} onClick={downloadIif}>
              Download IIF
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminQuickBooksExportPage;
